/**
 * What slaying the Starfall Wyrm pays (M40, docs/RARES.md; Kevin 2026-10-12: "if killed we should unlock a skin for
 * either the base or one of the towers, one the user doesn't have yet"; once all 13 are owned, "a Mythic chest + gems").
 * Pure: the sim does not know what the player owns, so the app asks this with the cosmetics' `owns` at the kill.
 *
 * - The Starfall set: the catalogue's skins whose unlock is "wyrm" (shop.json: one tower skin per tower type, worn only on
 *   that type, and one fortress skin), in catalogue order.
 * - A skin: one of those the player lacks, uniform, drawn from the battle's seed and the Wyrm's stage (tag "wyrmSkin":
 *   apart from every other roll), so a resumed or retried battle names the same skin.
 * - The set complete: a Mythic chest at `level` (the run's rare level: Endless's *through*, else the level) rolled from
 *   the same seed, and `wyrmRules.completeSetGems`.
 */
import { BalanceData, Rarity } from "../data/balance";
import { SkinData } from "../data/commerce";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Pcg32 } from "../maths/pcg32";
import { ChestResult, chestOfRarity } from "../sim/chest";

export type WyrmReward =
  | { readonly kind: "skin"; readonly skinId: string }
  | { readonly kind: "chest"; readonly chest: ChestResult; readonly gems: number };

const WYRM_SKIN_TAG = "wyrmSkin";

/** The Starfall skins, in catalogue order. */
export function starfallSkins(balance: BalanceData): SkinData[] { return balance.commerce.shop.skins.list.filter((s) => s.unlock.by === "wyrm"); }

/** The seed of a Wyrm's reward: its battle's seed and stage. */
export function wyrmRewardSeed(seed: bigint, stage: number): bigint {
  const h = new Fnv1a64();
  h.writeString(WYRM_SKIN_TAG);
  h.writeLong(seed);
  h.writeInt(stage);
  return h.value;
}

/** The reward of a Wyrm slain on `stage` of the battle with `seed`, given what the player owns (see the module note). */
export function wyrmReward(balance: BalanceData, owns: (skinId: string) => boolean, level: number, seed: bigint, stage: number): WyrmReward {
  const lacking = starfallSkins(balance).filter((s) => !owns(s.id));
  const roll = wyrmRewardSeed(seed, stage);
  if (lacking.length > 0) return { kind: "skin", skinId: lacking[new Pcg32(roll).nextInt(lacking.length)].id };
  return { kind: "chest", chest: chestOfRarity(balance, level, Rarity.Mythic, roll), gems: balance.spawnRules.wyrm.completeSetGems };
}
